/**
 * A small word-level diff (classic LCS dynamic programming) - draft
 * arguments run a few sentences, not whole documents, so an O(n*m) table
 * is more than fast enough and needs no external library.
 */
export type DiffSegment = { type: "same" | "added" | "removed"; text: string };

function tokenize(text: string): string[] {
  return text.match(/\S+|\s+/g) ?? [];
}

export function diffWords(original: string, current: string): DiffSegment[] {
  const a = tokenize(original);
  const b = tokenize(current);
  const n = a.length;
  const m = b.length;

  const lengths: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lengths[i][j] = a[i] === b[j] ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
    }
  }

  const segments: DiffSegment[] = [];
  function push(type: DiffSegment["type"], text: string) {
    const last = segments[segments.length - 1];
    if (last && last.type === type) last.text += text;
    else segments.push({ type, text });
  }

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push("same", a[i]);
      i += 1;
      j += 1;
    } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
      push("removed", a[i]);
      i += 1;
    } else {
      push("added", b[j]);
      j += 1;
    }
  }
  while (i < n) {
    push("removed", a[i]);
    i += 1;
  }
  while (j < m) {
    push("added", b[j]);
    j += 1;
  }

  return segments;
}
